<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productPrice.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productPrice.pageTitle'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
					<div class="ts-space-x-3">						
						<ts-button outline color="primary" @click.prevent="onShowForm">{{
							$t('copy')
						}}</ts-button>
						<ts-button outline color="info" @click.prevent="update">{{
							$t('update')
						}}</ts-button>
					</div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('productPrice.branchName') }}</label>
                  <ts-branch-filter
                    @filter="branchFilter"
                    class="ts-w-full"
                  ></ts-branch-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table
        :columns="columns"
        :records="resources"
        :loading="loading"
      >
        <template v-slot="{ record }">
          <td>{{ record.branch_name_en }}</td>
          <td>{{ record.pro_group_name }}</td>
          <td>{{ record.category_name_en }}</td>
          <td>{{ record.barcode }}</td>
          <td class="ts-font-khmer">{{ record.product_name_kh }}</td>
          <td>{{ record.product_name_en }}</td>
          <td>{{ record.sale_code }}</td>
          <td>{{ record.uom_name_en }}</td>
          <td class="text-center">{{ record.grade }}</td>
          <td class="ts-text-right">{{ record.cost }}</td>
          <td class="ts-text-right">{{ record.dealer_price }}</td>
          <td class="ts-text-right">{{ record.sale_price }}</td>
        </template>
      </ts-table>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
			<a-modal
				v-model="show_form"
				:centered="true"
				:closable="false"
				:zIndex="1020"
				:title="$t('productPrice.copyFrom')"
				:okText="$t('yes')"
				:cancelText="$t('no')"
				@ok="onCopyAction"
				@cancel="clearInput"
				:confirmLoading="waiting"
				width="350px"
			>
				<ts-loading-banner :loading="loading_branch">
					<div class="row">
						<div class="col-md-12">
							<label class="required">{{ $t('productPrice.branchName') }}</label>
							<a-select 
								v-model="model.copy_from_branch_id"
								showSearch
								:filterOption="(input, option) => option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0"
								:options="branches"
								class="ts-w-full"></a-select>
							<div class="validate" v-if="errors.has('copy_from_branch_id')">
								{{ errors.first('copy_from_branch_id')}}
							</div>
						</div>
					</div>
				</ts-loading-banner>
			</a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'

export default {
  name: 'productPrice',
  data() {
    return {
      loading: false,
			show_form: false,
			loading_branch: false,
			waiting: false,
			errors: new Errors(),
			branches: [],
			model: {
				copy_from_branch_id: null
			}
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('product/price', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.product.price.search
      },
      set(newValue) {
        this.$store.commit('product/price/SET_SEARCH', newValue)
        this.$store.commit('product/price/RESET_CURRENT_PAGE')
      },
    },
    columns() {
      return [
        { name: this.$t('productPrice.branchName') },
        { name: this.$t('productPrice.group') },
        { name: this.$t('productPrice.category') },
        { name: this.$t('productPrice.barcode') },
        { name: this.$t('productPrice.productNameKh') },
        { name: this.$t('productPrice.productNameEn') },
        { name: this.$t('productPrice.saleCode') },
        { name: this.$t('productPrice.uom') },
        { name: this.$t('productPrice.grade'), style: 'width:30px; text-align:center' },
        { name: this.$t('productPrice.cost'), style: 'text-align:right' },
        { name: this.$t('productPrice.dealerPrice'), style: 'text-align:right' },
        { name: this.$t('productPrice.salePrice'), style: 'text-align:right' },
      ]
    },
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('product/price/fetch', { ...attributes })
        .then((response) => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
		onShowForm(){
			this.show_form = true
			this.loading_branch = true
			this.$store.dispatch('product/price/getBranch')
				.then(response => {
					this.branches = response.data.map(el => ({
						value: el.branch_id,
						label: el.branch_name_en
					}))
				})
				.finally(() => {
					this.loading_branch = false
				})
		},
		onCopyAction(){
			this.waiting = true
			this.$store.dispatch('product/price/store', this.model)
				.then(response => {
					this.$toasted.success(response.message)
					this.fetchData()
					this.clearInput()
				})
				.catch(error => {
					this.errors = new Errors(error.errors)
				})
				.finally(() => {
					this.waiting = false
				})
		},
		clearInput(){
			this.show_form = false
			this.errors = new Errors()
			this.model.copy_from_branch_id = null
		},
    update() {
      this.$router.push({ name: 'product-price-update' })
    },
    branchFilter(value){
      this.$store.commit('product/price/SET_BRANCH_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('product/price/RESET_STATE')
    next()
  },
}
</script>
